import { http, createConfig, fallback } from 'wagmi'
import { mainnet, sepolia, polygon, hardhat } from 'wagmi/chains'
import { injected, walletConnect } from 'wagmi/connectors'

// Polygon Amoy testnet definition (not in wagmi by default)
const polygonAmoy = {
    id: 80002,
    name: 'Polygon Amoy',
    nativeCurrency: {
        decimals: 18,
        name: 'POL',
        symbol: 'POL',
    },
    rpcUrls: {
        default: { http: ['https://rpc-amoy.polygon.technology'] },
    },
    blockExplorers: {
        default: { name: 'PolygonScan', url: 'https://amoy.polygonscan.com' },
    },
    testnet: true,
} as const

// Reliable RPC transports with fallback for Polygon Amoy
const amoyTransport = fallback([
    http('https://polygon-amoy.drpc.org'),
    http('https://rpc-amoy.polygon.technology'),
    http('https://polygon-amoy-bor-rpc.publicnode.com'),
], {
    rank: true,
    retryCount: 2,
    retryDelay: 500,
})

// Only include Hardhat chain in development when local node is expected
const isDev = import.meta.env.DEV && typeof window !== 'undefined' && window.location.hostname === 'localhost'

/** Build a wagmi config with the given WalletConnect project ID (or null for injected-only). */
export function buildWagmiConfig(wcProjectId: string | null) {
    const connectors = wcProjectId
        ? [
            walletConnect({ projectId: wcProjectId, showQrModal: true }),
            injected(),
          ]
        : [injected()]

    return isDev
        ? createConfig({
            chains: [mainnet, polygon, polygonAmoy, sepolia, hardhat],
            connectors,
            transports: {
                [mainnet.id]: http(),
                [polygon.id]: http(),
                [polygonAmoy.id]: amoyTransport,
                [sepolia.id]: http(),
                [hardhat.id]: http(),
            },
        })
        : createConfig({
            chains: [mainnet, polygon, polygonAmoy, sepolia],
            connectors,
            transports: {
                [mainnet.id]: http(),
                [polygon.id]: http(),
                [polygonAmoy.id]: amoyTransport,
                [sepolia.id]: http(),
            },
        })
}

// Immediate config with build-time env (fallback; may be injected-only)
const buildTimeId = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID?.trim().match(/[a-f0-9]{32}/i)?.[0] ?? null
export const config = buildWagmiConfig(buildTimeId)

// Export chain IDs for easy reference
export const CHAIN_IDS = {
    MAINNET: 1,
    POLYGON: 137,
    POLYGON_AMOY: 80002,
    SEPOLIA: 11155111,
    HARDHAT: 31337,
} as const

// Betting is only available on Polygon networks
export const BETTING_CHAIN_IDS = [CHAIN_IDS.POLYGON, CHAIN_IDS.POLYGON_AMOY] as const
